import React, { FC, useState, useEffect, useRef } from 'react'
import { Send, Loader2, Bot, User, RotateCcw } from 'lucide-react'
import { MessageModel, UserInfoModel, defaultUserInfos } from '../../../_metronic/helpers'
import { getUserData } from '../../modules/auth'
import * as API from '../../api'
import DataVisualization from './DataVisualization'
import TypingIndicator from './TypingIndicator'
import FeedbackButtons from './FeedbackButtons'
import SuggestedQuestions from './SuggestedQuestions'
import { sha256 } from 'crypto-hash'

type Props = {
  isDrawer?: boolean
}

const formatMessageText = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;

  return text
    .replace(/\n/g, '<br/>')
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(urlRegex, (url) => {
      const href = url.startsWith('http') ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-700 underline break-all font-medium">${url}</a>`;
    });
}

const Chat: FC<Props> = ({ isDrawer = false }) => {
  const [chatUpdateFlag, toggleChatUpdateFlat] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [lastMessage, setLastMessage] = useState<string>('')
  const [messages, setMessages] = useState<MessageModel[]>([])
  const [userInfos] = useState<UserInfoModel[]>(defaultUserInfos)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState<boolean>(false)
  const [counter, setCounter] = useState(0)
  const [session, setSession] = useState<string>('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [user, setUser] = useState<any>(null)
  const [showSuggestions, setShowSuggestions] = useState(true)

  const firstAIMessage = 'Olá! Em que posso te ajudar?'
  const waitAIMessage = 'Aguarde, estou buscando informações na base de dados...'

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const createMessage = (isUser: Boolean, text: string, query: string = ''): MessageModel => {
    return {
      user: isUser ? 2 : 1,
      type: isUser ? 'out' : 'in',
      text: text,
      time: 'Just now',
      div: null,
      query
    }
  }

  const sendMessage = async (isUser: Boolean) => {
    if (!message.trim()) return
    const hash = await sha256(new Date().getTime().toString() + Math.random())
    const newMsg: MessageModel = createMessage(true, message)
    newMsg.hash = hash
    setMessages((prevMessages) => [...prevMessages, newMsg])
    toggleChatUpdateFlat(!chatUpdateFlag)
    setLastMessage(message)
    setMessage('')
    setShowSuggestions(false)
    if (isUser) {
      request(message)
    }
  }

  const handleSuggestionSelect = (suggestion: string) => {
    setMessage(suggestion)
    setShowSuggestions(false)
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 50)
  }

  const scrollToBottom = async () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const aiMessage = async (type: string, data: any, query: string = '') => {
    let message = null
    let tableContent = null
    if (type === 'text') {
      message = data
    } else if (type == 'ai') {
      if (data.hasQuery) {
        const columns = data.data.columns;
        let rows = data.data.rows;
        //if (rows) rows = rows.slice(0, 50);
        if (columns && rows) {
          tableContent = <DataVisualization key={Math.random()} columns={columns} rows={rows} />
          message = tableContent
        }
      } else {
        message = data.message;
      }      
    } else {
      const resultData = data.statement_response.result
      const manifestData = data.statement_response.manifest

      const columns = manifestData.schema.columns
      let rows = resultData.data_typed_array
      if (rows) rows = rows.slice(0, 50)
      if (columns && rows) {
        tableContent = <DataVisualization key={Math.random()} columns={columns} rows={rows} />
        message = tableContent
      }
    }
    const hash = await sha256(new Date().getTime().toString() + Math.random())
    const messageObj = createMessage(false, message, query)
    messageObj.hash = hash
    messageObj.div = tableContent
    setMessages((prevMessages) => [...prevMessages, messageObj])
    toggleChatUpdateFlat((flag) => !flag)
    return messageObj
  }

  const createSessionHash = async () => {
    const hash = await sha256(new Date().getTime().toString() + Math.random())
    setSession(hash)
    return
  }

  useEffect(() => {
    createSessionHash()
    setUser(getUserData())
  }, [])

  useEffect(() => {
    if (!session) return
    request('Olá')
  }, [session])

  const request = async (message: any) => {
    setIsRequesting(true)
    const waitingMessage = await aiMessage('text', '...');
    const response = await API.aiChat({ chatId: session, message, owner: user?.name, website: true });
    setMessages((prevMessages) =>
      prevMessages.filter((msg) => msg.hash !== waitingMessage.hash)
    )
    const aiMessageObj = await aiMessage(response.data.statement_response ? 'query' : response.data?.type == 'ai' ? 'ai' : 'text', response.data);

    aiMessageObj.text = response.data.message ? response.data.message : response.data;
    setIsRequesting(false)
  }

  const handleNewSession = async () => {
    setMessages([])
    setShowSuggestions(true)
    await createSessionHash()
  }

  const setMessageRating = async (message: MessageModel, index: number, value: string) => {
    message.rating = value
    setCounter(counter + 1)
    const user_message =
      messages[index - 1].type === 'in' ? messages[index - 2].text : messages[index - 1].text
    const ai_message = message.query !== '' ? '' : message.text
    const obj = {
      user_message,
      ai_message,
      query: message.query,
      is_positive: value === 'yes',
      hash: message.hash,
      session
    }
    await API.aiMessageRating(obj)
  }

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isRequesting) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(true)
    }
  }

  const autoResize = () => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 128) + 'px'
    }
  }

  const hasMessages = messages.length > 1;

  return (
    <div className="flex flex-col h-[calc(100vh-230px)]">
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
        {messages.map((msg, index) => {
          const isAI = msg.type === 'in';
          const isWaiting = msg.text === '...';

          return (
            <div
              key={`message-${index}`}
              className={`flex ${isAI ? '' : 'justify-end'} animate-fadeIn`}
            >
              <div
                className={`flex ${isAI ? 'flex-row' : 'flex-row-reverse'} ${
                  msg.div ? 'max-w-[95%]' : 'max-w-[80%]'
                }`}
              >
                <div className="flex-shrink-0">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shadow-sm ${
                      isAI
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                        : 'bg-gradient-to-br from-gray-600 to-gray-700'
                    }`}
                  >
                    {isAI ? (
                      <Bot className="w-5 h-5 text-white" />
                    ) : user?.src ? (
                      <img alt="Avatar" className="rounded-full w-9 h-9 object-cover" src={getUserData().src} />
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </div>
                </div>

                <div className={`flex flex-col ${isAI ? 'ml-3' : 'mr-3'} min-w-0`}>
                  <span className="text-xs font-medium text-gray-500 mb-1.5">
                    {isAI ? 'Compra AI' : getUserData()?.name}
                  </span>

                  {isWaiting ? (
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm">
                      <TypingIndicator />
                    </div>
                  ) : msg.div ? (
                    <div className="space-y-3">
                      {msg.div}
                      {msg.text !== firstAIMessage &&
                        msg.text !== waitAIMessage &&
                        isAI && (
                          <FeedbackButtons
                            message={msg}
                            index={index}
                            onRate={setMessageRating}
                          />
                        )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                          isAI
                            ? 'bg-gray-100 text-gray-900 rounded-tl-sm'
                            : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-tr-sm'
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: formatMessageText(msg.text)
                        }}
                      />
                      {msg.text !== firstAIMessage &&
                        msg.text !== waitAIMessage &&
                        isAI && (
                          <FeedbackButtons
                            message={msg}
                            index={index}
                            onRate={setMessageRating}
                          />
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      {showSuggestions && !isRequesting && hasMessages && (
        <div className="border-t border-gray-100">
          <SuggestedQuestions
            onSelect={handleSuggestionSelect}
            disabled={isRequesting}
          />
        </div>
      )}

      <div className="border-t border-gray-200 px-4 sm:px-6 py-4 bg-white/80 backdrop-blur-sm ">
        <div className="flex items-center gap-3">
          <button
            onClick={handleNewSession}
            className="flex-shrink-0 p-2.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            title="Nova conversa"
            aria-label="Iniciar nova conversa"
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              className="w-full min-h-[44px] max-h-32 rounded-xl border border-gray-300 px-4 py-3 pr-12 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-shadow"
              placeholder="Escreva uma mensagem..."
              rows={1}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                autoResize()
              }}
              onKeyDown={onEnterPress}
              aria-label="Campo de mensagem"
            />
          </div>

          <button
            className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            onClick={() => sendMessage(true)}
            disabled={isRequesting || !message.trim()}
            aria-label="Enviar mensagem"
          >
            {isRequesting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-2">
          Compra AI pode cometer erros. Considere verificar informações importantes.
        </p>
      </div>
    </div>
  )
}

export { Chat }
